import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { attachFrame, frameLike, makeFrontHalf, releaseFrontHalf, shareDecals, slideOff } from './laserHalves';
import { EDGE_STAINS, stainEdge } from './laserGore';

// Where the round head sits in each rig's head art (pixels).
const HEAD_ART = {
	Normal: {cx: 48, cy: 48.5, r: 34},
	Kid: {cx: 37.5, cy: 36, r: 28},
	Baby: {cx: 27.5, cy: 23.5, r: 20.5},
};
const FACE_TURN = 0.4;
const CUT_EDGE = 0x5a0a10;

const fromFrameSpace = (frame, x, y) => {
	const lx = (x - frame.pivot.x) * frame.scale.x;
	const ly = (y - frame.pivot.y) * frame.scale.y;
	const c = Math.cos(frame.rotation);
	const s = Math.sin(frame.rotation);
	return {x: (frame.x + c * lx - s * ly) / Settings.PTM, y: (frame.y + s * lx + c * ly) / Settings.PTM};
};

const toFrameSpace = (frame, x, y) => {
	const dx = x - frame.x;
	const dy = y - frame.y;
	const c = Math.cos(frame.rotation);
	const s = Math.sin(frame.rotation);
	return {
		x: (c * dx + s * dy) / frame.scale.x + frame.pivot.x,
		y: (-s * dx + c * dy) / frame.scale.y + frame.pivot.y,
	};
};

// The head art is drawn three-quarter on, so the depth cut runs down the middle
// of the face: between the eyes, through the nose. Seen from the front the cut
// face is an upright oval, narrower the more the face is turned towards us.
const faceGeometry = (humanoid, skin) => {
	const texture = skin.originalSprite.texture;
	const art = HEAD_ART[skin.data.textureName.split('_')[0]] ||
		{cx: texture.width / 2, cy: texture.height / 2, r: Math.min(texture.width, texture.height) * 0.36};
	const geometry = {...art, a: art.r * FACE_TURN, extent: Math.max(texture.width, texture.height) * 2};
	const eyes = ['eye_left', 'eye_right'].map(name => humanoid.lookupObject[name])
		.filter(eye => eye && !eye.destroyed && !eye.snapped && eye.myTexture && !eye.myTexture.destroyed);
	if (eyes.length === 2) {
		const middle = (toFrameSpace(skin, eyes[0].myTexture.x, eyes[0].myTexture.y).x + toFrameSpace(skin, eyes[1].myTexture.x, eyes[1].myTexture.y).x) / 2;
		geometry.a = Math.min(art.r * 0.8, Math.max(art.r * 0.15, middle - art.cx));
	}
	return geometry;
};

const faceArc = ({cx, cy, r, a}) => {
	const points = [];
	for (let i = 0; i <= 24; i++) {
		const t = -Math.PI / 2 + Math.PI * i / 24;
		points.push(cx + a * Math.cos(t), cy + r * Math.sin(t));
	}
	return points;
};

// Everything on one side of the face's midline, out past the edges of the art.
const faceSide = (geometry, near) => {
	const {cx, extent} = geometry;
	const arc = faceArc(geometry);
	if (near) return [-extent, -extent, cx, -extent, ...arc, cx, extent, -extent, extent];
	const reversed = [];
	for (let i = arc.length - 2; i >= 0; i -= 2) reversed.push(arc[i], arc[i + 1]);
	return [cx, -extent, extent, -extent, extent, extent, cx, extent, ...reversed];
};

const maskedLayer = (children, drawMask) => {
	const layer = new PIXI.Container();
	const mask = drawMask(new PIXI.Graphics());
	layer.addChild(...children, mask);
	layer.mask = mask;
	return layer;
};

// Fresh copies of the skin's layers: the meat under it (shown through carved
// wounds), the skin with its wounds and the mouth.
const skinLayers = (skin, mouth) => {
	const flesh = skin.myFlesh ? new PIXI.Sprite(skin.myFlesh.texture) : null;
	if (flesh) flesh.tint = skin.myFlesh.tint;
	const face = new PIXI.Sprite(skin.originalSprite.texture);
	let mouthCopy = null;
	if (mouth && !mouth.destroyed) {
		mouthCopy = new PIXI.Sprite(mouth.texture);
		mouthCopy.position.copyFrom(mouth.position);
		mouthCopy.anchor.copyFrom(mouth.anchor);
	}
	return {flesh, face, mouth: mouthCopy, list: [flesh, face, mouthCopy].filter(Boolean)};
};

const cutFace = (skin, geometry, organs) => {
	const {cx, cy, r, a} = geometry;
	const meat = new PIXI.Sprite(skin.myFlesh ? skin.myFlesh.texture : skin.originalSprite.texture);
	if (skin.myFlesh) meat.tint = skin.myFlesh.tint;
	const inside = [meat];
	if (organs) {
		const brain = new PIXI.Sprite(PIXI.Texture.from('Gore_Brain0000'));
		brain.anchor.set(0.5);
		brain.position.set(cx, cy - r * 0.38);
		brain.scale.set(a * 1.55 / brain.texture.width, r * 0.62 / brain.texture.height);
		inside.push(brain);
	}
	const layer = new PIXI.Container();
	layer.addChild(
		maskedLayer(inside, graphics => graphics.ellipse(cx, cy, a, r).fill(0xffffff)),
		new PIXI.Graphics().ellipse(cx, cy, a, r).stroke({width: 1.5, color: CUT_EDGE, alpha: 0.9}),
	);
	layer.meat = meat;
	return layer;
};

export const splitHead = (humanoid, back) => {
	const lookup = humanoid.lookupObject;
	const textures = game.editor.textures;
	const skin = back.myTexture;
	const geometry = faceGeometry(humanoid, skin);
	humanoid.dropMask?.();
	humanoid.hat?.detach();

	// The back half keeps the far side of the face, with its eye, around the cut face.
	const backFrame = frameLike(skin);
	const far = skinLayers(skin, humanoid.mouth);
	const inside = cutFace(skin, geometry, !humanoid.noGoreParticles);
	backFrame.addChild(inside, maskedLayer(far.list, graphics => graphics.poly(faceSide(geometry, false)).fill(0xffffff)));
	backFrame.originalSprite = far.face;
	backFrame.myFlesh = inside.meat;
	textures.addChildAt(backFrame, textures.getChildIndex(skin));
	attachFrame(back, backFrame, skin);
	let below = backFrame;
	const farEye = lookup.eye_right?.myTexture;
	if (farEye && !farEye.destroyed && farEye.parent === textures) {
		textures.setChildIndex(farEye, textures.getChildIndex(backFrame) + 1);
		below = farEye;
	}

	// The front half is the near side of the face, cut along the midline. It takes
	// the near eye along and slides off.
	const near = skinLayers(skin, humanoid.mouth);
	const frontFrame = frameLike(skin);
	frontFrame.addChild(
		maskedLayer(near.list, graphics => graphics.poly(faceSide(geometry, true)).fill(0xffffff)),
		new PIXI.Graphics().poly(faceArc(geometry), false).stroke({width: 1.5, color: CUT_EDGE, alpha: 0.9}),
	);
	frontFrame.originalSprite = near.face;
	frontFrame.myFlesh = near.flesh || near.face;
	if (near.mouth) humanoid.mouth = near.mouth;
	const front = makeFrontHalf(humanoid, back, 'head');
	textures.addChildAt(frontFrame, textures.getChildIndex(below) + 1);
	attachFrame(front, frontFrame, skin);
	shareDecals(back, front);
	releaseFrontHalf(humanoid, back, front, 'head');
	slideOff(front, back);

	// Both halves show the same wounded skin, so the stains land along the midline on either side.
	const arc = faceArc(geometry);
	const stains = [];
	for (let i = 0; i < EDGE_STAINS; i++) {
		const point = 2 * Math.round((i + 0.5) / EDGE_STAINS * (arc.length / 2 - 1));
		stains.push(fromFrameSpace(backFrame, arc[point], arc[point + 1]));
	}
	stainEdge(back, stains);
	skin.destroy({children: true});
};
